import { ChevronDownIcon } from '@/app/features/icons/ChevronDown'
import { useClickOutside } from '@/app/hooks/useClickOutside'
import { ContractDetailsModel } from '@/modules/drivers/types/contracts'
import { useStableId } from '@/ui/form/hooks/useStableId'
import { Input } from '@/ui/form/primitives'
import * as React from 'react'

type FieldInputProps = React.ComponentProps<typeof Input> & {
  label?: string
  helper?: React.ReactNode
  labelRight?: React.ReactNode
  error?: string | null
  innerLabel?: string
  field: keyof ContractDetailsModel
}
export type FieldAndInputProps = {
  fieldOptions: FieldOptions
  inputProps: FieldInputProps
  selectProps: FieldSelectProps
  children?: React.ReactNode
  allowDecimal?: boolean
}
export type FieldOptions = {
  inputSize?: 'small' | 'big'
  fullWidth?: boolean
  label?: string
  required?: boolean
  helper?: React.ReactNode
  error?: string | null
}

export type SelectOption = {
  key?: string | number
  label: string
  value: string
  content?: React.ReactNode
  icon?: React.ElementType
}

type FieldSelectProps = {
  innerLabel?: string
  placeholder?: string
  field: keyof ContractDetailsModel
  icon?: React.ElementType
  options: Array<SelectOption>
  className?: string
  value?: string | number | null
  error?: string | boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>
  handleChange?: (
    value: string,
    props: { name: string; option: SelectOption },
  ) => void
  name?: string
  disabled?: boolean
  children?: React.ReactNode
  required?: boolean
}

const NumberAndSelectInnerField = ({
  inputProps,
  selectProps,
  fieldOptions,
  allowDecimal = true,
}: FieldAndInputProps) => {
  const autoId = useStableId()
  const inputId = `autoid-${autoId}`
  const helperId = fieldOptions?.helper ? `${inputId}-helper` : undefined
  const errorId = fieldOptions?.error ? `${inputId}-error` : undefined
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined

  // Local display state preserves intermediate values like "1." while typing,
  // even if the parent converts the value to a number (stripping the trailing dot).
  const [localInputValue, setLocalInputValue] = React.useState<string>(
    inputProps?.value?.toString() || '',
  )
  const isFocused = React.useRef(false)

  React.useEffect(() => {
    if (!isFocused.current) {
      setLocalInputValue(inputProps?.value?.toString() || '')
    }
  }, [inputProps?.value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    let sanitized: string
    if (allowDecimal) {
      sanitized = val.replace(/,/g, '.').replace(/[^0-9.]/g, '')
      const parts = sanitized.split('.')
      if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('')
      }
      // max 2
      if (parts.length === 2 && parts[1].length > 2) {
        sanitized = parts[0] + '.' + parts[1].slice(0, 2)
      }
    } else {
      sanitized = val.replace(/[^0-9]/g, '')
    }

    setLocalInputValue(sanitized)

    if (inputProps.onChange) {
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: sanitized,
        },
      } as React.ChangeEvent<HTMLInputElement>
      inputProps.onChange(newEvent)
    }
  }

  const handleInputFocus = () => {
    isFocused.current = true
  }

  const handleInputBlur = () => {
    isFocused.current = false
    setLocalInputValue(inputProps?.value?.toString() || '')
  }

  const isBigInput = fieldOptions?.inputSize === 'big'
  const [selectValue, setSelectValue] = React.useState<string | number>(
    selectProps?.value ?? '',
  )
  const [dropdownOpen, setDropdownOpen] = React.useState(false)
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1)
  const selectedOption =
    selectProps?.options?.find((o) => o.value === selectValue) || null

  const toggleDropdown = () => {
    if (!selectProps?.disabled) setDropdownOpen(!dropdownOpen)
  }

  const handleSelectOption = (option: SelectOption) => {
    setDropdownOpen(false)
    setSelectValue(option?.value)

    if (selectProps.handleChange) {
      selectProps.handleChange(option?.value, {
        name: selectProps.name!,
        option,
      })
    }

    if (selectProps.onChange) {
      const event = {
        target: {
          name: selectProps.name,
          value: option?.value,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>
      selectProps.onChange(event)
    }
  }

  React.useEffect(() => {
    if (
      selectProps?.value !== undefined &&
      selectProps?.value !== selectValue
    ) {
      setSelectValue(selectProps.value)
    }
  }, [selectProps?.value])

  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)

  useClickOutside({
    ref: dropdownRef,
    handler: () => setDropdownOpen(false),
  })

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!dropdownOpen) {
      if (e.key.length === 1 && /[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(e.key)) {
        setDropdownOpen(true)
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev + 1) % selectProps.options.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex(
        (prev) =>
          (prev - 1 + selectProps.options.length) % selectProps.options.length,
      )
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (focusedIndex >= 0)
        handleSelectOption(selectProps.options[focusedIndex])
    } else if (e.key.length === 1) {
      const letter = e.key.toLowerCase()
      const foundIndex = selectProps.options.findIndex((o) =>
        o.label.toLowerCase().startsWith(letter),
      )
      if (foundIndex >= 0) {
        setFocusedIndex(foundIndex)
        const li = listRef.current?.children[foundIndex] as HTMLElement
        li?.scrollIntoView({ block: 'nearest' })
      }
    } else if (e.key === 'Escape') {
      setDropdownOpen(false)
    }
  }

  React.useEffect(() => {
    if (dropdownOpen && focusedIndex >= 0) {
      const li = listRef.current?.children[focusedIndex] as HTMLElement
      li?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, dropdownOpen])

  return (
    <div
      data-control="text-field"
      className={`tw-flex tw-flex-col tw-gap-1 ${fieldOptions?.fullWidth ? 'tw-w-full' : ''}`}
    >
      {fieldOptions?.label && (
        <label className="tw-m-0 tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">
          {fieldOptions.required && (
            <span className="tw-mr-0.5 tw-text-error">*</span>
          )}
          {fieldOptions.label}
        </label>
      )}
      <div className="tw-flex">
        <div
          className={`tw-border-e-none tw-relative tw-flex tw-items-stretch tw-rounded-md tw-rounded-e-none tw-border tw-bg-white ${fieldOptions?.fullWidth ? 'tw-w-full' : 'tw-max-w-[150px]'}`}
        >
          {inputProps?.innerLabel && (
            <label
              htmlFor={inputId}
              className={`tw-m-0 tw-flex tw-select-none ${
                isBigInput
                  ? 'tw-items-center tw-text-sm'
                  : 'tw-items-end tw-pb-1 tw-text-xs'
              } tw-pl-2 tw-font-medium tw-text-content-base`}
            >
              {inputProps.innerLabel}
            </label>
          )}
          <Input
            id={inputId}
            aria-describedby={describedBy}
            aria-invalid={!!fieldOptions?.error || undefined}
            value={localInputValue}
            placeholder={inputProps?.placeholder}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            className={`tw-block tw-w-full tw-rounded-md ${isBigInput ? 'tw-px-3 tw-py-2' : 'tw-px-2 tw-py-1'} tw-text-sm tw-leading-tight tw-placeholder-gray-400 tw-shadow-sm disabled:tw-cursor-not-allowed disabled:tw-bg-gray-100 disabled:tw-text-gray-400 data-[invalid=true]:tw-border-red-500 data-[invalid=true]:focus:tw-ring-red-500`}
          />
        </div>

        <div
          className={`tw-border-s-none tw-relative tw-flex tw-items-stretch tw-rounded-md tw-rounded-s-none tw-border tw-bg-white ${fieldOptions?.fullWidth ? 'tw-w-full' : 'tw-max-w-[150px]'}`}
        >
          <div
            className={`tw-relative tw-w-full ${selectProps?.className || ''}`}
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="combobox"
            aria-expanded={dropdownOpen}
            aria-haspopup="listbox"
            aria-controls="dropdown-list"
          >
            <button
              type="button"
              onClick={toggleDropdown}
              className={`focus:tw-ring-s-none tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-bg-white tw-px-2 tw-text-left tw-transition-all ${!dropdownOpen ? 'hover:tw-opacity-80' : ''} ${isBigInput ? 'tw-py-2' : 'tw-py-1'}`}
            >
              <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium">
                {selectProps?.icon && (
                  <span className="tw-p-0.5">
                    <selectProps.icon className="tw-h-4 tw-w-4" />
                  </span>
                )}
                {selectedOption ? (
                  selectedOption?.label
                ) : (
                  <span className="tw-text-xs tw-font-normal tw-text-input">
                    {selectProps?.placeholder}
                  </span>
                )}
              </span>
              <ChevronDownIcon
                className={`tw-h-4 tw-w-4 tw-transition-transform ${dropdownOpen ? 'tw-rotate-180' : ''}`}
              />
            </button>

            {dropdownOpen && (
              <ul
                ref={listRef}
                className="custom-scrollbar hide-scrollbar tw-absolute tw-z-[200] tw-mt-2 tw-flex tw-max-h-[200px] tw-w-full tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-md tw-border tw-border-content-base tw-border-opacity-20 tw-bg-white tw-px-1.5 tw-py-1.5 tw-text-content-base tw-shadow-multi"
              >
                {selectProps?.options.map((option, idx) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => handleSelectOption(option)}
                      className={`tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-md tw-px-1 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-300 hover:tw-bg-opacity-80 focus:tw-bg-base-300 focus:tw-bg-opacity-80 focus:tw-outline-none ${selectedOption?.value === option?.value ? '!tw-bg-secondary tw-bg-opacity-80 tw-font-medium' : ''} ${focusedIndex === idx ? 'tw-bg-base-300 tw-bg-opacity-80' : ''}`}
                    >
                      <span className="tw-flex tw-items-start tw-gap-1 tw-text-left">
                        {option.icon && (
                          <span className="tw-p-1">
                            <option.icon className="tw-h-3" />
                          </span>
                        )}
                        <span>
                          {option?.content || option?.label || option?.value}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default NumberAndSelectInnerField
